import React, { Component } from "react";
import { DiscreteColorLegend } from "react-vis";
import ReactResizeDetector from "react-resize-detector";

import { color, defaultLabel, makeRanking, decreasingExponential } from "utils";

const ResponsiveSpan = ({ children, width, max, ranks, fontSize }) => {
  fontSize.max = Math.min(width / String(max).length, fontSize.max);
  const value = children;
  const rank = ranks.find((rank) => rank.value === value)?.rank;
  const maxRank = Math.max(...ranks.map((item) => item.rank)) || 1;
  const currentFontSize = decreasingExponential(fontSize.min, maxRank, fontSize.max)(rank) || 0;
  return (
    <span
      style={{
        width,
        fontSize: Math.min(currentFontSize, fontSize.max),
        // border: '1px solid red'
      }}
    >
      {value}
    </span>
  );
};

// Composant graphique pour afficher un chart en pyramide.
// Réglages possibles à base de ratios.
// La plus grande valeur est prise comme référence et doit passer dans le sommet de la pyramide (même si elle n'est pas dedans).
// Utiliser `textWidthRatio` pour configurer la largeur du texte dans les étages de la pyramide, il faut que ça passe dans le sommet.
// La taille de police des autres valeurs suit une exponentielle décroissante.
// La valeur du nombre n'est pas utilisée dans le calcul de la taille de police, seulement le rang du nombre l'est (ex aequo possible).
// Aucun nombre ne peut être plus grand en taille que le nombre max, idem pour les nombres petits et le min.
// Ce qui est pris en compte pour la largeur du texte est le nombre de chiffres dans le nombre. Pas sa vraie largeur ou sa valeur.
class Pyramid extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hint: false,
      data: [],
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.refreshes === prevState.refreshes || !nextProps.data) return null;

    var data = nextProps.data || [];
    data = data.map((d, i) => ({
      ...d,
      label: d.label ? String(d.label) : defaultLabel,
      color: color(i, d.label, nextProps.colorOffset),
    }));

    return {
      data,
      refreshes: nextProps.refreshes,
    };
  }

  render() {
    const { hint } = this.state;
    let { aspectRatio, textWidthRatio, fontSizeRatio } = this.props;

    // Config.
    aspectRatio = aspectRatio || 0.7;
    textWidthRatio = textWidthRatio || 1 / 5;
    fontSizeRatio = {
      min: fontSizeRatio?.min || 1 / 4.5,
      max: fontSizeRatio?.max || 1 / 2.5,
    };

    const data = [...this.state.data];

    // Logique pour les effets du hover.
    (data || []).map((d) => {
      if (hint) {
        d.className = d.label === hint.label ? "hovered" : "not-hovered";
      } else {
        d.className = "";
      }
    });

    const partCount = data.length;
    const partHeightPerc = 100 / partCount;
    const values = data.map((d) => d.value);
    const max = Math.max(...values);
    const ranks = [...makeRanking(values)];

    return (
      <ReactResizeDetector handleWidth>
        {({ width }) => {
          width = width || 0;
          const height = aspectRatio * width;
          const partHeight = 0.01 * partHeightPerc * height;
          return (
            <div className="pyramid-chart-wrap">
              <DiscreteColorLegend
                orientation={this.props.legendOrientation}
                items={(data || []).map((d, i) => ({
                  title: d.label,
                  color: d.color,
                  disabled: d.className === "not-hovered",
                  hint: { ...d },
                }))}
                onItemMouseEnter={(item) => this.setState({ hint: item.hint })}
                onItemMouseLeave={() => this.setState({ hint: false })}
              />
              <div
                className={"pyramid-chart" + (this.props.inverted ? " inverted" : "")}
                style={{ height, ...this.props.style }}
              >
                {data.map((d, i) => (
                  <div
                    key={i}
                    className="part"
                    onMouseEnter={() => this.setState({ hint: { ...d } })}
                    onMouseLeave={() => this.setState({ hint: false })}
                    style={{
                      top: `${i * partHeightPerc}%`,
                      height: `${partHeightPerc}%`,
                      background: d.color,
                      opacity: !hint || d.label === hint.label ? 1 : 0.5,
                    }}
                  >
                    <ResponsiveSpan
                      width={textWidthRatio * width}
                      max={max}
                      ranks={ranks}
                      fontSize={{
                        min: fontSizeRatio.min * partHeight,
                        max: fontSizeRatio.max * partHeight,
                      }}
                    >
                      {d.value}
                    </ResponsiveSpan>
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      </ReactResizeDetector>
    );
  }
}

export default Pyramid;
